import * as stylex from "@stylexjs/stylex";

import { badge, button, layout, modal, spacing, text } from "@animus/primaris";

import type { ModifierDefinition, ModifierEffect } from "./types.ts";
import { styles } from "./styles.stylex.ts";

function getCategoryBadgeStyle(category: string) {
  switch (category) {
    case "cooking":
      return badge.blue;
    case "preparation":
      return badge.purple;
    case "addition":
      return badge.orange;
    default:
      return badge.neutral;
  }
}

function formatEffectValue(key: string, value: number): string {
  if (key.includes("Multiplier")) {
    return `×${value}`;
  }
  if (value > 0) {
    return `+${value}`;
  }
  return String(value);
}

function formatEffectLabel(key: string): string {
  const labels: Record<string, string> = {
    yieldMultiplier: "Yield",
    fatAdd: "Fat",
    fatMultiplier: "Fat ×",
    carbsAdd: "Carbs",
    proteinAdd: "Protein",
    fiberAdd: "Fiber",
    saturatedFatAdd: "Sat. Fat",
    sodiumAdd: "Sodium",
  };
  return labels[key] ?? key;
}

interface EffectsGridProps {
  effects: ModifierEffect;
}

function EffectsGrid({ effects }: EffectsGridProps) {
  const entries = Object.entries(effects).filter(
    ([, value]) => value !== undefined
  );

  if (entries.length === 0) {
    return <p {...stylex.props(text.muted, text.sm)}>No effects</p>;
  }

  return (
    <div {...stylex.props(styles.effectsGrid)}>
      {entries.map(([key, value]) => (
        <div {...stylex.props(styles.effectItem)} key={key}>
          <div {...stylex.props(styles.effectValue, text.white)}>
            {formatEffectValue(key, value as number)}
          </div>
          <div {...stylex.props(styles.effectLabel, text.muted)}>
            {formatEffectLabel(key)}
          </div>
        </div>
      ))}
    </div>
  );
}

interface ModifierDetailModalProps {
  modifier: ModifierDefinition;
  onClose: () => void;
}

function ModifierDetailModal({ modifier, onClose }: ModifierDetailModalProps) {
  return (
    <div {...stylex.props(modal.overlay)}>
      <div {...stylex.props(modal.container, styles.wideModal)}>
        <div {...stylex.props(modal.header)}>
          <div>
            <h2 {...stylex.props(text.xl, text.bold, text.white)}>
              {modifier.name}
            </h2>
            <p {...stylex.props(text.sm, text.muted)}>{modifier.slug}</p>
          </div>
          <button
            {...stylex.props(modal.close)}
            onClick={onClose}
            type="button"
          >
            &times;
          </button>
        </div>

        <div {...stylex.props(styles.modalBody)}>
          <p {...stylex.props(text.muted, spacing.mb4)}>
            {modifier.description}
          </p>

          <div
            {...stylex.props(layout.flexRow, layout.gap2, styles.detailSection)}
          >
            <span
              {...stylex.props(
                badge.base,
                getCategoryBadgeStyle(modifier.category)
              )}
            >
              {modifier.category}
            </span>
            {modifier.exclusiveGroup && (
              <span {...stylex.props(badge.base, badge.neutral)}>
                {modifier.exclusiveGroup}
              </span>
            )}
          </div>

          <div {...stylex.props(styles.detailSection)}>
            <h3
              {...stylex.props(
                text.sm,
                text.bold,
                text.muted,
                styles.detailLabel
              )}
            >
              Default Effects
            </h3>
            <EffectsGrid effects={modifier.defaultEffects} />
          </div>

          {modifier.rules.length > 0 && (
            <div {...stylex.props(styles.detailSection)}>
              <h3
                {...stylex.props(
                  text.sm,
                  text.bold,
                  text.muted,
                  styles.detailLabel
                )}
              >
                Tag-Based Rules ({modifier.rules.length})
              </h3>
              {modifier.rules.map((rule) => (
                <div
                  {...stylex.props(styles.ruleCard)}
                  key={[...rule.tagMatch].sort().join(",")}
                >
                  <div {...stylex.props(styles.ruleHeader)}>
                    <span {...stylex.props(text.sm, text.muted)}>
                      When tags match:
                    </span>
                    <div {...stylex.props(styles.tagContainer)}>
                      {rule.tagMatch.map((tag) => (
                        <span
                          {...stylex.props(badge.base, badge.green)}
                          key={tag}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <EffectsGrid effects={rule.effects} />
                  {rule.note && (
                    <p {...stylex.props(text.sm, text.muted, styles.ruleNote)}>
                      {rule.note}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div {...stylex.props(modal.footer)}>
          <button
            {...stylex.props(button.base, button.secondary)}
            onClick={onClose}
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export { ModifierDetailModal, type ModifierDetailModalProps };
